/**
 * Shared Zod schemas for every JSON fixture the dashboard consumes.
 *
 * Each schema is exported alongside a `guard*` helper that returns a
 * `Result<T>` instead of throwing — the surrounding hook can render an
 * error chip without unwinding the React tree. Schemas are deliberately
 * loose (`.loose()`) at the top level so unknown ancillary fields the
 * Python generator may add do not break the UI.
 */

import { z } from "zod";
import { GENERATOR_CODES, NEUTRALIZATION_MODE_NAMES } from "@csk/core";
import type {
  BridgeMatrix as CoreBridgeMatrix,
  NeutralizationModeName,
} from "@csk/core";
import {
  normalizeBridgeMatrixRules,
  type BridgeMatrixIndex,
} from "./bridgeMatrixIndex";
import type { Result } from "./lib";

const nonEmptyString = z.string().min(1);
const finiteNumber = z.number();
const generatorSchema = z.enum(GENERATOR_CODES);
const modeSchema = z.enum(NEUTRALIZATION_MODE_NAMES);
const statusSchema = z.enum(["allowed", "forbidden"]);
const constraintOpSchema = z.enum(["<=", "<", ">=", ">", "=="]);

const bridgeSchema = z
  .object({
    bridge_id: nonEmptyString,
    mode: modeSchema.optional(),
    generator: generatorSchema.optional(),
    seam: nonEmptyString.optional(),
    fills: z.array(nonEmptyString).optional(),
    requires_witness: z.array(nonEmptyString).optional(),
    notes: z.string().optional(),
  })
  .loose();

const matrixRuleSchema = z
  .object({
    lhs: nonEmptyString,
    rhs: nonEmptyString,
    status: statusSchema,
    reason: z.string().optional(),
    required_witnesses: z.array(nonEmptyString).optional(),
    required_gates: z.array(nonEmptyString).optional(),
    notes: z.string().optional(),
  })
  .loose();

const matrixSchema = z
  .object({
    version: nonEmptyString.optional(),
    rules: z.array(matrixRuleSchema),
    unknown_pair: statusSchema.optional(),
  })
  .loose();

const generatorCoordinatesSchema = z
  .object({
    G1: finiteNumber,
    G2: finiteNumber,
    G3: finiteNumber,
    G4: finiteNumber,
  })
  .strict();

const footprintSchema = z
  .object({
    system_id: nonEmptyString,
    coordinates: generatorCoordinatesSchema,
  })
  .loose();

const ledgerEntrySchema = z
  .object({
    ts: finiteNumber,
    kind: nonEmptyString,
    file: nonEmptyString,
    payload_hash: nonEmptyString,
    prev_head: z
      .string()
      .nullable()
      .optional()
      .transform((value) => value ?? null),
    hash: nonEmptyString,
  })
  .loose();

const ledgerSchema = z
  .object({
    version: nonEmptyString.optional(),
    head: z
      .string()
      .nullable()
      .optional()
      .transform((value) => value ?? null),
    entries: z.array(ledgerEntrySchema),
  })
  .loose();

const ruleChangeSchema = z
  .object({
    lhs: nonEmptyString,
    rhs: nonEmptyString,
    from: z.string().nullable(),
    to: z.string().nullable(),
  })
  .loose();

const deltaDiffSchema = z
  .object({
    added_bridges: z.array(nonEmptyString),
    removed_bridges: z.array(nonEmptyString),
    rule_changes: z.array(ruleChangeSchema),
    footprint_A: generatorCoordinatesSchema,
    footprint_B: generatorCoordinatesSchema,
    footprint_delta: generatorCoordinatesSchema,
  })
  .loose();

const regionConstraintSchema = z
  .object({
    op: constraintOpSchema,
    axis: generatorSchema,
    value: finiteNumber,
  })
  .strict();

const regionSchema = z
  .object({
    region_id: nonEmptyString,
    V: nonEmptyString,
    constraints: z.array(regionConstraintSchema),
    notes: z.string().optional(),
  })
  .loose();

const proofTreeSchema: z.ZodType<ProofTree> = z.lazy(() =>
  z
    .object({
      label: nonEmptyString,
      ok: z.boolean().nullable(),
      detail: z.string().optional(),
      children: z.array(proofTreeSchema).optional(),
      pins: z.record(z.string(), z.string()).optional(),
    })
    .loose(),
);

const receiptSchema = z.record(z.string(), z.unknown());

interface ZodIssueLike {
  readonly path: readonly unknown[];
  readonly message: string;
}

type SafeParseResult<T> =
  | { readonly success: true; readonly data: T }
  | {
      readonly success: false;
      readonly error: { readonly issues: readonly ZodIssueLike[] };
    };

export type Bridge = z.infer<typeof bridgeSchema>;
export type MatrixRule = z.infer<typeof matrixRuleSchema>;
type BridgeMatrixPayload = z.infer<typeof matrixSchema>;
export type BridgeMatrix = BridgeMatrixPayload & {
  readonly core: CoreBridgeMatrix;
  readonly index: BridgeMatrixIndex;
};
export type Footprint = z.infer<typeof footprintSchema>;
export type LedgerEntry = z.infer<typeof ledgerEntrySchema>;
export type Ledger = z.infer<typeof ledgerSchema>;
export type GeneratorCoordinates = z.infer<typeof generatorCoordinatesSchema>;
export type DeltaDiff = z.infer<typeof deltaDiffSchema>;
export type RegionConstraint = z.infer<typeof regionConstraintSchema>;
export type Region = z.infer<typeof regionSchema>;
export type Receipt = z.infer<typeof receiptSchema>;
export type BridgeMode = NeutralizationModeName;

export interface ProofTree {
  label: string;
  ok: boolean | null;
  detail?: string;
  children?: ProofTree[];
  pins?: Record<string, string>;
}

function formatPath(path: readonly unknown[]): string {
  if (path.length === 0) return "<root>";
  return path
    .map((part) =>
      typeof part === "number" ? `[${String(part)}]` : String(part),
    )
    .join(".");
}

function fromSafeParse<T>(
  label: string,
  result: SafeParseResult<T>,
): Result<T> {
  if (result.success) return { ok: true, value: result.data };
  const first = result.error.issues[0];
  return {
    ok: false,
    error: `${label}: ${formatPath(first.path)} ${first.message}`,
  };
}

export function guardBridges(x: unknown): Result<Bridge[]> {
  return fromSafeParse("bridges", z.array(bridgeSchema).safeParse(x));
}

export function guardMatrix(x: unknown): Result<BridgeMatrix> {
  const parsed = matrixSchema.safeParse(x);
  if (!parsed.success) return fromSafeParse("matrix", parsed);
  const normalized = normalizeBridgeMatrixRules(
    parsed.data.rules,
    parsed.data.unknown_pair ?? "forbidden",
  );
  if (normalized === null) {
    return {
      ok: false,
      error: "matrix: failed core bridge-matrix validation",
    };
  }
  return { ok: true, value: { ...parsed.data, ...normalized } };
}

export function guardFootprint(x: unknown): Result<Footprint> {
  return fromSafeParse("footprint", footprintSchema.safeParse(x));
}

export function guardLedger(x: unknown): Result<Ledger> {
  return fromSafeParse("ledger", ledgerSchema.safeParse(x));
}

export function guardMissingBridge(x: unknown): Result<Bridge> {
  return fromSafeParse("missing_bridge", bridgeSchema.safeParse(x));
}

export function guardDeltaDiff(x: unknown): Result<DeltaDiff> {
  return fromSafeParse("delta_diff", deltaDiffSchema.safeParse(x));
}

export function guardRegion(x: unknown): Result<Region> {
  return fromSafeParse("region", regionSchema.safeParse(x));
}

export function guardProofTree(x: unknown): Result<ProofTree> {
  return fromSafeParse("proof_tree", proofTreeSchema.safeParse(x));
}

export function guardReceipt(x: unknown): Result<Receipt> {
  return fromSafeParse("receipt", receiptSchema.safeParse(x));
}
